import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTheme } from './ThemeContext';
import { Icons } from './icons';

// ============================================================================
// ОПРОСНИК ДЛЯ НАСТАВНИКА
// ----------------------------------------------------------------------------
// Ответы НЕ используются для ИИ — только наставнику.
// Один вопрос — один экран. Ответы вводит сам человек, свободным текстом;
// в поле показывается подсказка (placeholder), это не тест и не выбор варианта.
// Прогресс сохраняется при закрытии, при следующем открытии — с того же вопроса.
// ============================================================================

// 18 вопросов, пять блоков. Вопрос «во сколько встаёте и ложитесь» убран
// по решению владельца 2026-09-12 — остальные сдвинуты, нумерация сквозная.
export const QUESTIONS = [
  { id: 1,  block: 'Цель',          title: 'Какой вес хотите?',                        hint: 'Например: 78 кг' },
  { id: 2,  block: 'Цель',          title: 'Какую талию хотите?',                      hint: 'Например: 80 см' },

  { id: 3,  block: 'Питание',       title: 'Сколько приёмов пищи и перекусов в день?', hint: 'Например: 3 приёма и 2 перекуса' },
  { id: 4,  block: 'Питание',       title: 'Во сколько самое позднее время едите или перекусываете?', hint: 'Например: в 20:00' },
  { id: 5,  block: 'Питание',       title: 'Питаетесь домашней едой или вне дома?',    hint: 'Например: дома, но обеды на работе' },
  { id: 6,  block: 'Питание',       title: 'Кто готовит дома?',                        hint: 'Например: сам / жена / мама / покупаете готовое' },
  { id: 7,  block: 'Питание',       title: 'На какие продукты есть аллергия?',         hint: 'Если нет — так и напишите. Например: молоко, орехи' },
  { id: 8,  block: 'Питание',       title: 'Какие продукты не любите?',                hint: 'Например: печень, творог' },
  { id: 9,  block: 'Питание',       title: 'Какие белковые продукты готовы есть каждый день?', hint: 'Например: курица, яйца, рыба' },

  { id: 10, block: 'Сложности',     title: 'Что мешает питаться правильно?',           hint: 'Например: работа, нет времени готовить' },
  { id: 11, block: 'Сложности',     title: 'Бывают ли срывы? Если да — в какое время и после чего?', hint: 'Например: вечером после стресса на работе' },
  { id: 12, block: 'Сложности',     title: 'Заедаете ли стресс?',                      hint: 'Например: да, сладким' },

  { id: 13, block: 'Здоровье',      title: 'Какие есть хронические заболевания?',      hint: 'Если нет — так и напишите' },
  { id: 14, block: 'Здоровье',      title: 'Какие лекарства принимаете?',              hint: 'Если нет — так и напишите' },
  { id: 15, block: 'Здоровье',      title: 'Сколько воды пьёте в день?',               hint: 'Например: около 1,5 литра' },

  { id: 16, block: 'Образ жизни',   title: 'Есть ли ночные смены?',                    hint: 'Например: нет / 2 раза в неделю' },
  { id: 17, block: 'Образ жизни',   title: 'Сколько часов спите в сутки?',             hint: 'Например: 6–7 часов' },
  { id: 18, block: 'Образ жизни',   title: 'Какая физическая активность в день?',      hint: 'Например: ходьба, зал 2 раза в неделю' },
];

export const QUESTIONNAIRE_TOTAL = QUESTIONS.length;

// Ключ локального черновика — страховка, если облако не успело получить запись
const LOCAL_DRAFT_KEY = 'questionnaireDraft';

// Черновик из localStorage — читается, если серверного прогресса ещё нет.
export const readLocalDraft = () => {
  try {
    const raw = localStorage.getItem(LOCAL_DRAFT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) { return null; }
};

export const clearLocalDraft = () => {
  try { localStorage.removeItem(LOCAL_DRAFT_KEY); } catch (e) {}
};

// Продолжение опроса: номер вопроса из сохранённого прогресса, в границах списка.
export const resumeIndex = (progress) => {
  const n = Number(progress);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.min(Math.floor(n), QUESTIONS.length - 1);
};

function Questionnaire({ getAuth, initialAnswers = {}, initialSkipped = [], initialProgress = 0, initialCompleted = false, onSave, onSaveProgress, onClose }) {
  const { colors } = useTheme();

  const [answers, setAnswers] = useState(() => ({ ...initialAnswers }));
  const [skipped, setSkipped] = useState(() => [...initialSkipped]);
  const [index, setIndex] = useState(() => resumeIndex(initialProgress));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const q = QUESTIONS[index];
  const last = index === QUESTIONS.length - 1;
  const qKey = String(q.id);

  // Ответ на текущий вопрос. Отдельного «грязного» состояния нет: правка сразу
  // ложится в answers — так крестик закрывает опрос без потери набранного текста.
  const value = answers[qKey] || '';

  const setValue = (text) => {
    setAnswers(prev => {
      const next = { ...prev };
      if (text === '') delete next[qKey]; else next[qKey] = text;
      return next;
    });
    // Начал писать — вопрос больше не «пропущен»
    if (text !== '') setSkipped(prev => prev.filter(id => id !== q.id));
  };

  // SVG-кнопка «Удалить» — очищает поле. Есть и при первом прохождении (п. 7 ТЗ).
  const clearAnswer = () => {
    setAnswers(prev => {
      const next = { ...prev };
      delete next[qKey];
      return next;
    });
    setSkipped(prev => prev.filter(id => id !== q.id));
  };

  const goNext = () => {
    // «Далее» без ответа = пропуск вопроса (п. 5 ТЗ: пропустить можно любой).
    if (value.trim() === '' && !skipped.includes(q.id)) {
      setSkipped(prev => [...prev, q.id]);
    }
    setIndex(i => Math.min(i + 1, QUESTIONS.length - 1));
  };

  const goBack = () => setIndex(i => Math.max(i - 1, 0));

  const skip = () => {
    setSkipped(prev => prev.includes(q.id) ? prev : [...prev, q.id]);
    setIndex(i => Math.min(i + 1, QUESTIONS.length - 1));
  };

  // Сохранение: ответы заменяют старые целиком (п. 7 ТЗ), пометка пропусков — свежая.
  const handleSave = async () => {
    setSaving(true);
    const payload = {
      answers,
      skipped: QUESTIONS.filter(x => skipped.includes(x.id)).map(x => x.id),
      progress: 0,
      completed: true,
      updatedAt: new Date().toISOString(),
    };
    try {
      if (onSave) await onSave(payload);
      setSaved(true);
    } finally {
      setSaving(false);
    }
  };

  // --- Сохранение прогресса при закрытии ----------------------------------
  // БАГ, который был: закрытие крестиком ничего не сохраняло — все ответы и
  // номер вопроса жили только в памяти компонента, и при следующем открытии
  // человек начинал с первого вопроса с пустыми полями. Теперь закрытие
  // отправляет прогресс в облако (не завершая опрос), а страховкой ещё и в
  // localStorage — на случай, если сеть или токен не сработали.

  // Собирает текущий снимок. completed сохраняет прежнее значение: закрытие
  // на середине НЕ должно помечать опрос пройденным — иначе наставник увидит
  // «Пройден» у человека, который просто вышел на 8-м вопросе.
  const snapshot = () => ({
    answers,
    skipped: QUESTIONS.filter(x => skipped.includes(x.id)).map(x => x.id),
    progress: index,
    completed: !!(initialCompleted || saved),
    updatedAt: new Date().toISOString(),
  });

  const wasDraftSaved = useRef(false);

  // Закрытие: сначала пишем черновик в облако, потом закрываем экран.
  // Ждём запись — за это время сохраняется текущий ответ (последние нажатия
  // тоже успевают уйти, потому что текст пишется в answers сразу).
  const handleClose = async () => {
    if (saved) { if (onClose) onClose(); return; }
    if (wasDraftSaved.current) { if (onClose) onClose(); return; }
    wasDraftSaved.current = true;
    try {
      if (onSaveProgress) await onSaveProgress(snapshot());
    } catch (e) {
      console.error('Не удалось сохранить черновик опроса:', e);
    }
    if (onClose) onClose();
  };

  // Страховка на случай закрытия вкладки или перезагрузки страницы:
  // beforeunload не ждёт сеть, поэтому пишем только локальный черновик —
  // он подхватится при следующем открытии, и человек напишет последний ответ заново.
  useEffect(() => {
    const onBeforeUnload = () => {
      if (saved) return;
      try { localStorage.setItem(LOCAL_DRAFT_KEY, JSON.stringify(snapshot())); } catch (e) {}
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers, skipped, index, saved]);

  // «Спасибо, ответы сохранены» — 3 секунды, затем экран закрывается (п. 8 ТЗ).
  useEffect(() => {
    if (!saved) return;
    const t = setTimeout(() => { if (onClose) onClose(); }, 3000);
    return () => clearTimeout(t);
  }, [saved, onClose]);

  const progressPct = useMemo(() => Math.round(((index + 1) / QUESTIONS.length) * 100), [index]);

  const answeredCount = Object.values(answers).filter(v => (v || '').trim() !== '').length;

  const st = {
    root: {
      height: '100%', width: '100%', display: 'flex', flexDirection: 'column',
      background: colors.page, color: colors.text, fontFamily: 'system-ui, -apple-system, sans-serif',
      overflow: 'hidden', boxSizing: 'border-box', position: 'relative'
    },
    header: { display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', background: colors.header, flexShrink: 0 },
    headerBtn: {
      background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff',
      padding: '6px 10px', borderRadius: 10, cursor: 'pointer',
      display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, flexShrink: 0
    },
    headerTitle: { fontSize: 16, fontWeight: 600, color: '#fff', flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    headerSub: { fontSize: 11, color: 'rgba(255,255,255,0.65)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    // Прогресс-бар визуальный: на логику не влияет (п. 13 ТЗ)
    barWrap: { height: 4, background: colors.border, flexShrink: 0 },
    barFill: { height: '100%', width: `${progressPct}%`, background: '#1D9E75', transition: 'width 0.25s' },
    body: { flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '20px 16px 12px', display: 'flex', flexDirection: 'column' },
    block: { fontSize: 12, fontWeight: 700, color: '#1D9E75', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 },
    question: { fontSize: 20, fontWeight: 600, lineHeight: 1.35, marginBottom: 16 },
    fieldRow: { position: 'relative', display: 'flex', alignItems: 'flex-start', gap: 8 },
    textarea: {
      flex: 1, width: '100%', minHeight: 110, padding: '12px 14px', fontSize: 15, lineHeight: 1.45,
      border: `1px solid ${colors.inputBorder}`, borderRadius: 12, background: colors.inputBg,
      color: colors.text, boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit'
    },
    delBtn: {
      background: 'transparent', border: 'none', cursor: 'pointer', padding: 4,
      opacity: 0.75, flexShrink: 0, alignSelf: 'flex-start', marginTop: 4
    },
    counters: { marginTop: 10, fontSize: 12, color: colors.textMuted },
    // Кнопки — одним рядом сразу под текстовым полем (решение владельца 2026-09-12)
    navRow: { display: 'flex', gap: 8, alignItems: 'stretch', marginTop: 16 },
    backBtn: {
      flex: '0 0 auto', padding: '13px 14px', background: colors.card, color: colors.text,
      border: `1px solid ${colors.border}`, borderRadius: 12, fontSize: 15, fontWeight: 500,
      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6
    },
    nextBtn: {
      flex: 1, padding: '13px 16px', background: '#1D9E75', color: '#fff', border: 'none',
      borderRadius: 12, fontSize: 15, fontWeight: 600, cursor: 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6
    },
    skipBtn: {
      flex: '0 0 auto', padding: '13px 14px', background: 'transparent', color: colors.textMuted,
      border: `1px solid ${colors.border}`, borderRadius: 12, fontSize: 15, fontWeight: 500,
      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'
    },
    overlay: {
      position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 40,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24
    },
    thanks: { background: colors.card, borderRadius: 20, padding: '28px 24px', textAlign: 'center', maxWidth: 340, boxShadow: '0 8px 32px rgba(0,0,0,0.25)' },
  };

  const skippedNow = skipped.includes(q.id) && value.trim() === '';

  return (
    <div style={st.root}>
      <div style={st.header}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={st.headerTitle}>Опрос</div>
          <div style={st.headerSub}>Вопрос {index + 1} из {QUESTIONS.length}</div>
        </div>
        {/* Крестик — вверху справа (решение владельца 2026-09-12) */}
        <button style={st.headerBtn} onClick={handleClose} title="Закрыть опрос — прогресс сохранится">
          <Icons.close size={15} color="#fff" />
        </button>
      </div>

      <div style={st.barWrap}><div style={st.barFill} /></div>

      <div style={st.body}>
        <div style={st.block}>{q.block}</div>
        <div style={st.question}>{q.title}</div>

        <div style={st.fieldRow}>
          <textarea
            style={st.textarea}
            placeholder={q.hint}
            value={value}
            onChange={e => setValue(e.target.value)}
          />
          {/* SVG-кнопка «Удалить» — очищает поле (п. 7 ТЗ) */}
          <button style={st.delBtn} onClick={clearAnswer} title="Удалить ответ">
            <Icons.delete size={18} color="#e53935" />
          </button>
        </div>

        <div style={st.counters}>
          {skippedNow ? 'Вопрос пропущен — можно вернуться и ответить позже.' : `Отвечено: ${answeredCount} из ${QUESTIONS.length}`}
        </div>

        {/* Назад / Пропустить / Далее — в один ряд сразу под полем */}
        <div style={st.navRow}>
          <button style={{ ...st.backBtn, opacity: index === 0 ? 0.45 : 1, cursor: index === 0 ? 'default' : 'pointer' }}
            onClick={goBack} disabled={index === 0} title="Назад">
            <Icons.back size={15} color={colors.text} /> Назад
          </button>
          {!last && (
            <button style={st.skipBtn} onClick={skip} title="Пропустить вопрос">Пропустить</button>
          )}
          {last ? (
            <button style={{ ...st.nextBtn, opacity: saving ? 0.6 : 1, cursor: saving ? 'default' : 'pointer' }}
              onClick={handleSave} disabled={saving}>
              <Icons.save size={16} color="#fff" /> {saving ? 'Сохранение…' : 'Сохранить'}
            </button>
          ) : (
            <button style={st.nextBtn} onClick={goNext}>
              Далее <Icons.chevronRight size={16} color="#fff" />
            </button>
          )}
        </div>
      </div>

      {saved && (
        <div style={st.overlay}>
          <div style={st.thanks}>
            <div style={{ marginBottom: 10 }}><Icons.check size={40} color="#1D9E75" /></div>
            <div style={{ fontSize: 17, fontWeight: 600 }}>Спасибо, ответы сохранены</div>
            <div style={{ fontSize: 13, color: colors.textMuted, marginTop: 8 }}>Наставник увидит их в вашей карточке.</div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Questionnaire;
